import * as v from "valibot";
import type { UserForgotPasswordForm } from "~/lib/schemas/user-auth";
import { codeSchema, UserForgotPasswordSchema } from "~/lib/schemas/user-auth";
import { Link } from "@builder.io/qwik-city";
import { LuChevronLeft } from "@qwikest/icons/lucide";
import { routeLoader$, useNavigate } from "@builder.io/qwik-city";
import { $, useStore, useSignal, component$ } from "@builder.io/qwik";
import { isClerkAPIResponseError } from "~/lib/clerk";
import { useForm, getValue, validate, valiForm$ } from "@modular-forms/qwik";

import { Card } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Button } from "~/components/ui/button";
import { FormStep } from "~/components/ui/form-step";
import { OtpInput } from "~/components/ui/otp-input";
import { FormField } from "~/components/ui/form-field";

import type { InitialValues } from "@modular-forms/qwik";
import { useClerk } from "~/hooks/use-clerk";

export const useFormLoader = routeLoader$<
  InitialValues<UserForgotPasswordForm>
>(() => ({
  email: "",
  password: "",
  password2: "",
}));

export default component$(() => {
  const [form, { Field, Form }] = useForm<UserForgotPasswordForm>({
    loader: useFormLoader(),
    validate: valiForm$(UserForgotPasswordSchema),
  });
  const nav = useNavigate();
  const clerk = useClerk();

  const isProcessing = useSignal(false);
  const state = useStore({
    code: ["", "", "", "", "", ""],
    error: "",
    resendTimer: 60,
    canResend: false,
  });

  // OTP handlers
  const startResendTimer = $(() => {
    state.resendTimer = 60;
    state.canResend = false;
    const timer = setInterval(() => {
      state.resendTimer--;
      if (state.resendTimer <= 0) {
        state.canResend = true;
        clearInterval(timer);
      }
    }, 1000);
  });

  const currentStepIndex = useSignal(0);

  const handleSendOtp = $(async (formState: typeof form) => {
    if (!(await validate(form, ["email"]))) return;
    startResendTimer();
    state.error = "";
    isProcessing.value = true;
    try {
      if (!clerk.value) return;

      const email = getValue(formState, "email");
      if (!email) {
        state.error = "Email is required.";
        return;
      }

      await clerk.value.client!.signIn.create({
        identifier: email,
        strategy: "reset_password_email_code",
      });

      isProcessing.value = false;
      currentStepIndex.value = 1;
    } catch (err) {
      if (isClerkAPIResponseError(err)) {
        state.error =
          err.errors[0]?.longMessage || "Failed to create sign up attempt";
      } else {
        state.error = "An unexpected error occurred.";
      }
      isProcessing.value = false;
    }
  });

  const handleValidateCode = $(async () => {
    try {
      v.parse(codeSchema, { code: state.code });
      state.error = "";
      return true;
    } catch (error: any) {
      state.error = error.issues?.[0]?.message || "Invalid input";
      return false;
    }
  });

  const handleVerifyOtp = $(async () => {
    if (!(await validate(form, ["password", "password2"]))) return;
    isProcessing.value = true;
    try {
      if (!clerk?.value?.client) return;
      const password = getValue(form, "password");
      if (!password) {
        state.error = "Password is required.";
        return;
      }
      const verify = await clerk.value.client.signIn.attemptFirstFactor({
        strategy: "reset_password_email_code",
        code: state.code.join(""),
        password,
      });

      if (verify.status === "complete" && verify.createdSessionId) {
        // set clerkUserId to form and submit to process update user data on the server
        await clerk.value.setActive({ session: verify.createdSessionId });
        await nav("/settings");
      }
    } catch (err) {
      isProcessing.value = false;
      if (isClerkAPIResponseError(err)) {
        state.error = err.errors[0]?.longMessage || "Failed to verify OTP code";
        if (
          err.errors.length === 1 &&
          err.errors.some((err) => err.code === "form_code_incorrect")
        ) {
          currentStepIndex.value = 1;
        }
      } else {
        state.error = "An unexpected error occurred.";
      }

      state.error = "Failed to verify OTP code";
    }
  });

  const handleOtpChange = $((code: string[]) => {
    state.code = [...code];
  });

  const handleOtpComplete = $(async (code: string) => {
    state.code = code.split("");
    if (!(await handleValidateCode())) return;

    currentStepIndex.value = 2;
  });

  return (
    <Form>
      <Card class="sm:w-145 w-100 flex flex-col p-8 hover:shadow">
        <FormStep.Content stepPosition={currentStepIndex.value}>
          <div class="w-full flex-shrink-0 space-y-8 px-1">
            <div class="space-y-2">
              <Link
                href="/signin"
                class="text-sm text-gray-500 hover:text-gray-700"
              >
                <span class="inline-flex items-center">
                  <LuChevronLeft /> Log In
                </span>
              </Link>
              <h2 class="text-xl font-semibold">Forgot password</h2>
            </div>
            <Field name="email">
              {(field, props) => (
                <div class="space-y-2">
                  <FormField
                    fullWidth
                    label="Enter email or phone number"
                    error={field.error || state.error}
                  >
                    <Input
                      {...props}
                      type="email"
                      placeholder="Enter email or phone number"
                      value={field.value}
                      error={field.error}
                    />
                  </FormField>
                </div>
              )}
            </Field>
            <Button
              type="button"
              loading={isProcessing.value}
              disabled={isProcessing.value}
              class="mb-2 w-full"
              onClick$={() => handleSendOtp(form)}
            >
              Reset Password
            </Button>
            <div id="clerk-captcha" class="w-full" />
          </div>
          <div class="w-full flex-shrink-0 px-1">
            <div class="space-y-2">
              <h1 class="font-heading text-3xl">Check your email</h1>
              <h3 class="text-secondary font-heading">
                {`We sent a code to ${getValue(form, "email")}. Enter 6 digit code that mentioned in the email`}
              </h3>
            </div>
            <div class="my-8 flex items-end gap-4 transition-all duration-300 ease-in-out">
              <OtpInput
                value={state.code}
                error={state.error}
                onChange$={handleOtpChange}
                onComplete$={handleOtpComplete}
              />
            </div>
            <Button
              type="button"
              class="mb-2 w-full"
              onClick$={() => handleSendOtp(form)}
              disabled={!state.canResend}
              loading={isProcessing.value}
            >
              {state.canResend
                ? "Resend code"
                : `Resend code (${state.resendTimer}s)`}
            </Button>
          </div>
          <div class="w-full flex-shrink-0 space-y-8 px-1">
            <div class="space-x-2">
              <h1 class="font-heading text-3xl">Set a New Password</h1>
              <h3 class="text-secondary font-heading">
                Create a new password. Ensure it differs from previous ones for
                security
              </h3>
            </div>
            <div>
              <Field name="password">
                {(field, props) => (
                  <div class="space-y-2">
                    <FormField
                      fullWidth
                      label="Create Password"
                      error={field.error}
                    >
                      <Input
                        {...props}
                        autocomplete="off"
                        type="password"
                        placeholder="Create Password"
                        value={field.value}
                        error={field.error}
                      />
                    </FormField>
                  </div>
                )}
              </Field>
              <Field name="password2">
                {(field, props) => (
                  <div class="space-y-2">
                    <FormField
                      fullWidth
                      label="Confirm Password"
                      error={field.error}
                    >
                      <Input
                        {...props}
                        autocomplete="off"
                        type="password"
                        placeholder="Confirm Password"
                        value={field.value}
                        error={field.error}
                      />
                    </FormField>
                  </div>
                )}
              </Field>
            </div>
            <Button
              type="button"
              class="mb-2 w-full"
              loading={isProcessing.value}
              disabled={isProcessing.value}
              onClick$={() => {
                handleVerifyOtp();
              }}
            >
              Update Password
            </Button>
          </div>
        </FormStep.Content>
        <div class="font-400 mt-4 text-center text-sm">
          Don't have an account?{" "}
          <a href="/signup" class="text-primary/100 font-600 hover:underline">
            Sign up
          </a>
        </div>
      </Card>
    </Form>
  );
});
